import { Collapse, Flex, Text, theme } from '@/components';
import {
  type AnalysisSearchFormType,
  AnalysisSearchSchema,
} from '@/features/search/_models/analysis-search';
import { useForm } from '@/hooks';

import { useAnalysisPageSearchQueryStates } from '@/features/analysis/_hooks/search-query-states';
import { useAnalysisSheetType } from '@/features/analysis/_hooks/useAnalysisSheetType';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useProjectsSearchStore } from '../../_stores/projects-search-store';
import { SearchForm } from './SearchForm';
import { AggregationTags } from './tags/AggregationTags';
import { AnalysisSearchSearchFieldTags } from './tags/AnalysisSearchSearchFieldTags';

const getYearValue = (year: number | null) => {
  if (!year) {
    return undefined;
  }
  return dayjs().year(year);
};

export const AnalysisSearchAccordion: FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const prevUrl = useRef<string | null>(null);
  const { pathname, search, hash } = useLocation();
  const currentUrl = `${pathname}${search}${hash}`;

  const sheetType = useAnalysisSheetType();
  const { searchQueryStates } = useAnalysisPageSearchQueryStates();
  const { cascaderTargetIds } = useProjectsSearchStore();
  const prevCascaderTargetIds = useRef<typeof cascaderTargetIds | null>(null);
  const { openBasicAccordion, setOpenBasicAccordion } =
    useProjectsSearchStore();
  // 支出先の検索ワードをリセットするため。booleanだと一瞬の変化で検知できないため数値で比較
  const [resetCount, setResetCount] = useState(0);

  const defaultValues = useMemo<AnalysisSearchFormType>(() => {
    return {
      ...searchQueryStates,
      // sheetType はルート（/analysis/review-sheet | /analysis/fund-sheet）から決まる読み取り専用値。
      // 共通検索フィールド（ImplementationMethodsColField 等）が useWatch('sheetType') で
      // 表示判定するため、フォーム状態にも反映させる。
      sheetType,
      period: [
        getYearValue(searchQueryStates.startYear),
        getYearValue(searchQueryStates.endYear),
      ],
      nestedOrganizationIds: cascaderTargetIds.organizationIds ?? null,
      nestedOtherResponsibleOrganizationIds:
        cascaderTargetIds.otherResponsibleOrganizationIds ?? null,
      nestedExpenditureBudgetPurposeIds:
        cascaderTargetIds.expenditureBudgetPurposeIds ?? null,
      nestedPolicyIds: cascaderTargetIds.policyIds ?? null,
      fiscalYearRange: [
        dayjs().year(searchQueryStates.fiscalYearRange.at(0) ?? 0),
        dayjs().year(searchQueryStates.fiscalYearRange.at(1) ?? 0),
      ],
      analysisAxis:
        searchQueryStates.analysisAxis === 'year'
          ? null
          : searchQueryStates.analysisAxis,
    };
  }, [cascaderTargetIds, searchQueryStates, sheetType]);

  const formMethod = useForm<AnalysisSearchFormType>({
    resolver: zodResolver(AnalysisSearchSchema),
  });

  const onResetPayment = useCallback(() => {
    setResetCount((v) => v + 1);
  }, []);

  useEffect(() => {
    if (
      prevUrl.current !== currentUrl ||
      prevCascaderTargetIds.current !== cascaderTargetIds
    ) {
      formMethod.reset(defaultValues);
      prevUrl.current = currentUrl;
      prevCascaderTargetIds.current = cascaderTargetIds;
    }
  }, [currentUrl, defaultValues, formMethod, cascaderTargetIds]);

  return (
    <FormProvider {...formMethod}>
      <Collapse
        accordion
        activeKey={openBasicAccordion ? '1' : undefined}
        expandIcon={({ isActive }) =>
          isActive ? (
            <DownOutlined style={{ color: token.colorPrimary, fontSize: 12 }} />
          ) : (
            <RightOutlined
              style={{ color: token.colorPrimary, fontSize: 12 }}
            />
          )
        }
        onChange={(e) => {
          setOpenBasicAccordion(!!e.length);
        }}
        style={{
          background: token.colorBgBase,
          borderRadius: 0,
          minHeight: 80,
        }}
        items={[
          {
            id: 'filter-project-panel',
            key: '1',
            style: { borderRadius: 0 },
            label: (
              <>
                <>
                  <Text color="primary" text={t('search-condition')} />
                  {!openBasicAccordion && (
                    <Flex vertical gap={8}>
                      <AnalysisSearchSearchFieldTags
                        onResetPayment={onResetPayment}
                      />
                      <AggregationTags />
                    </Flex>
                  )}
                </>
              </>
            ),
            children: (
              <SearchForm
                onResetPayment={onResetPayment}
                resetCount={resetCount}
              />
            ),
          },
        ]}
      />
    </FormProvider>
  );
};
